import { usePostHog } from '@posthog/react';
import ListCreateButton from 'components/buttons/ListCreateButton';
import AddNewPrepaidInvoiceFormModal, {
    AddNewPrepaidInvoiceForm,
} from 'components/modals/invoices/AddNewPrepaidInvoiceFormModal';
import { useModal } from 'components/modals/useModal';
import { useNavigate } from 'react-router-dom';
import { useCreatePrepaidInvoiceMutation } from 'store/api/invoices';
import { useGetMattersListQuery } from 'store/api/matters';
import { MatterAmountDto } from 'types/invoices';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';

type Props = {
    matterId: string;
};

const MatterNewPrepaymentButton = ({ matterId }: Props) => {
    const posthog = usePostHog();
    const navigate = useNavigate();
    const { openModal, closeModal } = useModal();
    const [createPrepaid] = useCreatePrepaidInvoiceMutation();
    const { data: matterQueryData } = useGetMattersListQuery({
        pageSize: 1,
        page: 0,
        filters: `id==${matterId}`,
    });
    const initialMatter = matterQueryData?.content.at(0);

    const handleAddNewPrepaidInvoice = async (formData: AddNewPrepaidInvoiceForm) => {
        const selectedMatterId = formData.matter?.id;
        if (!selectedMatterId) {
            errorAlert(t('forms.error-submitting-the-form'));
            return;
        }

        const body: MatterAmountDto = {
            matterId: selectedMatterId,
            amount: formData.prepaymentSum ?? '0.00',
        };

        await createPrepaid(body)
            .unwrap()
            .then((resp) => {
                posthog.capture('invoice_created', { type: 'prepaid' });
                if (resp.id) navigate(`/invoice/${resp.id}`);
            })
            .catch(console.error);
    };

    const openAddNewPrepaidInvoiceModal = () => {
        openModal(
            <AddNewPrepaidInvoiceFormModal
                initialMatter={initialMatter}
                onSubmit={(data) => {
                    void handleAddNewPrepaidInvoice(data);
                }}
                onClose={closeModal}
            />,
            closeModal
        );
    };

    return (
        <ListCreateButton
            permissions={['CREATE.INVOICES.MATTER_ACCESS', 'CREATE.INVOICES.TYPE_PREPAYMENT_WITH_MATTER_ACCESS']}
            variant="outlined"
            size="small"
            disabled={!initialMatter}
            onClick={openAddNewPrepaidInvoiceModal}
            labelId="invoices.new-prepayment"
            newLabelId="new-entity.prepayment"
        />
    );
};

export default MatterNewPrepaymentButton;
